//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';

import Icon from '@components/atoms/icon/Icon';
import { AlertBox } from '@components/primitives/alert/AlertBox';
import { ITrackableComponent } from '@utils/withTracking';

type AlertType = Required<
  Pick<React.ComponentProps<typeof AlertBox.Root>, 'type'>
>['type'];

const AlertIcons: Record<AlertType, UnisonIconType> = {
  error: 'error',
  info: 'lightbulb',
  success: 'circle-checkmark',
  warning: 'alarm',
};

interface Props
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent {
  className?: string;
  type: AlertType;
  onDismiss?: () => void;
}

/**
 * The `Alert` atom renders a piece of text alongside an icon, that is displayed
 * within an AlertBox using a standard background, border, and
 * color combination, depending on the 'type' supplied to the `Alert` (e.g. warning / success / error / info).
 *
 * The alert will have a close button displayed as well if `onDismiss` is provided.
 *
 * Note: this is based on the `AlertBox` primitive.
 */
export const Alert = React.forwardRef<HTMLDivElement, Props>(
  ({ className, children, type, onDismiss, trackingId }, ref) => {
    const iconName: UnisonIconType = AlertIcons[type];
    return (
      <AlertBox.Root className={className} ref={ref} type={type}>
        <AlertBox.Icon>
          <Icon name={iconName} />
        </AlertBox.Icon>
        <AlertBox.Content>{children}</AlertBox.Content>
        {onDismiss && (
          <AlertBox.DismissButton
            onDismiss={onDismiss}
            trackingId={trackingId}
            type={type}
          />
        )}
      </AlertBox.Root>
    );
  },
);

Alert.displayName = 'Alert';
